/**
 * Das Tagebuch schreibt sich selbst (18.10.2026): was an einem Tag schon in
 * der App steht — Training, Essen, Wasser, Termine, Erledigtes — als
 * Rückschau, und der Monat mit Stimmung, Training und Fotos auf einen Blick.
 *
 * Nur lesen, nichts ableiten, was nicht dasteht: Ein Tag ohne Eintrag bleibt
 * ein Tag ohne Eintrag, er bekommt keine erfundene Farbe.
 */

import { addDays, isoDateInZone, weekdayOf } from '@kraftwerk/shared';
import type { IsoDate } from '@kraftwerk/shared';
import { db } from './local.js';
import type {
  KraftwerkDatabase,
  LocalJournalEntry,
  LocalJournalNote,
  LocalJournalPhoto,
} from './local.js';
import { readProfile } from './profile.js';

export interface DayStory {
  trainings: { title: string; minutes: number | null; sets: number }[];
  food: { kcal: number; protein: number; entries: number; proteinTarget: number | null } | null;
  waterMl: number;
  events: { title: string; time: string | null }[];
  tasksDone: string[];
  /** Bucketliste: an diesem Tag erlebt. */
  bucketDone: string[];
}

export async function dayStory(
  date: string,
  timeZone: string,
  database: KraftwerkDatabase = db,
): Promise<DayStory> {
  const [sessions, sets, food, water, events, shared, feed, completions, profile, bucket] =
    await Promise.all([
      database.workout_sessions.toArray(),
      database.workout_sets.toArray(),
      database.food_log.where('date').equals(date).toArray(),
      database.water_log.where('date').equals(date).toArray(),
      database.events.where('date').equals(date).toArray(),
      database.household_events.where('date').equals(date).toArray(),
      database.feed_events.where('date').equals(date).toArray(),
      database.task_completions.toArray(),
      readProfile(database),
      database.bucket_items.toArray(),
    ]);

  const daySessions = sessions.filter(
    (session) =>
      !session.deletedAt && isoDateInZone(new Date(session.startedAt), timeZone) === date,
  );
  const trainings = daySessions.map((session) => ({
    title: session.title,
    minutes: session.endedAt
      ? Math.round(
          (new Date(session.endedAt).getTime() - new Date(session.startedAt).getTime()) / 60_000,
        )
      : null,
    sets: sets.filter((set) => set.sessionId === session.id && !set.deletedAt).length,
  }));

  const eaten = food.filter((row) => !row.deletedAt);
  const foodSummary =
    eaten.length === 0
      ? null
      : {
          kcal: Math.round(eaten.reduce((sum, row) => sum + row.kcal, 0)),
          protein: Math.round(eaten.reduce((sum, row) => sum + row.protein, 0)),
          entries: eaten.length,
          proteinTarget: profile.targets?.protein ?? null,
        };

  const allEvents = [
    ...events.filter((row) => !row.deletedAt && !row.cancelled),
    ...shared.filter((row) => !row.deletedAt && !row.cancelled),
  ].map((row) => ({ title: row.title, time: row.time }));
  const feedEvents = feed
    .filter((row) => !row.deletedAt)
    .map((row) => ({
      title: row.title,
      time: row.allDay ? null : new Date(row.startsAt).toTimeString().slice(0, 5),
    }));

  return {
    trainings,
    food: foodSummary,
    waterMl: water.filter((row) => !row.deletedAt).reduce((sum, row) => sum + row.ml, 0),
    events: [...allEvents, ...feedEvents].sort((a, b) =>
      (a.time ?? '').localeCompare(b.time ?? ''),
    ),
    tasksDone: completions
      .filter((row) => !row.deletedAt && isoDateInZone(new Date(row.doneAt), timeZone) === date)
      .map((row) => row.title),
    bucketDone: bucket
      .filter((row) => !row.deletedAt && row.status === 'done' && row.doneDate === date)
      .map((row) => row.title),
  };
}

export interface MonthDay {
  date: string;
  mood: number | null;
  energy: number | null;
  trained: boolean;
  photos: boolean;
  notes: boolean;
}

/** Alle Tage eines Monats (`YYYY-MM`) mit Stimmung, Training, Fotos und Gedanken. */
export async function monthDays(
  month: string,
  timeZone: string,
  database: KraftwerkDatabase = db,
): Promise<MonthDay[]> {
  const first = `${month}-01`;
  const days: string[] = [];
  for (let date = first; date.startsWith(month); date = addDays(date as IsoDate, 1)) {
    days.push(date);
  }
  const last = days.at(-1)!;
  const [entries, sessions, notes, photos] = await Promise.all([
    database.journal_entries.where('date').between(first, last, true, true).toArray(),
    database.workout_sessions.toArray(),
    database.journal_notes.where('date').between(first, last, true, true).toArray(),
    database.journal_photos.toArray(),
  ]);
  const entryOf = new Map(entries.filter((row) => !row.deletedAt).map((row) => [row.date, row]));
  const trained = new Set(
    sessions
      .filter((session) => !session.deletedAt)
      .map((session) => isoDateInZone(new Date(session.startedAt), timeZone)),
  );
  const noteDays = new Set(notes.filter((row) => !row.deletedAt).map((row) => row.date));
  const entryById = new Map(entries.map((row) => [row.id, row.date]));
  const noteById = new Map(notes.map((row) => [row.id, row.date]));
  const photoDays = new Set(
    photos
      .filter((photo) => !photo.deletedAt)
      .map((photo) =>
        photo.noteId ? noteById.get(photo.noteId) : entryById.get(photo.journalEntryId ?? ''),
      )
      .filter((date): date is string => typeof date === 'string'),
  );
  return days.map((date) => {
    const entry = entryOf.get(date);
    return {
      date,
      mood: entry?.mood ?? null,
      energy: entry?.energy ?? null,
      trained: trained.has(date),
      photos: photoDays.has(date),
      notes: noteDays.has(date),
    };
  });
}

/** Ein Tag der Zeitleiste „Tage“ (20.10.2026) — alles, was es zu ihm gibt. */
export interface TimelineDay {
  date: string;
  entry: LocalJournalEntry | null;
  notes: number;
  firstNote: { title: string | null; body: string } | null;
  photos: number;
  firstPhoto: LocalJournalPhoto | null;
  trainings: string[];
  /** Gegessen in kcal — `null` heißt nichts eingetragen, nicht 0. */
  kcal: number | null;
  waterMl: number;
  events: number;
  bucketDone: string[];
}

/**
 * Steht im Befinden etwas? Ein Gedanke oder Foto legt die Tageszeile leer an
 * (`saveNote`, `addPhoto`) — eine leere Zeile ist kein Eintrag.
 */
export function hasCheckin(entry: LocalJournalEntry | null): boolean {
  if (!entry) return false;
  return (
    entry.mood !== null ||
    entry.energy !== null ||
    entry.sleepHours !== null ||
    entry.sleepQuality !== null ||
    entry.soreness !== null ||
    (entry.note ?? '') !== '' ||
    typeof entry.bodyWeightKg === 'number'
  );
}

/** Steht an dem Tag irgendetwas? Ein leerer Tag wird zusammengefasst, nie erfunden. */
export function isEmptyDay(day: TimelineDay): boolean {
  return (
    !hasCheckin(day.entry) &&
    day.notes === 0 &&
    day.photos === 0 &&
    day.trainings.length === 0 &&
    day.kcal === null &&
    day.waterMl === 0 &&
    day.events === 0 &&
    day.bucketDone.length === 0
  );
}

/**
 * Jeder Tag von `today` zurück bis `from`, neueste zuerst.
 *
 * Bis 20.10.2026 listete „Tage“ nur Tage mit Befinden-Zeile: Ein Tag mit nur
 * einem Gedanken oder nur einem Training fehlte, und Fotos an Gedanken
 * zählten nicht. Flach abgefragt (eine Ebene `await`), damit die
 * Live-Abfrage beobachtet bleibt.
 */
export async function journalTimeline(
  from: string,
  today: string,
  timeZone: string,
  database: KraftwerkDatabase = db,
): Promise<TimelineDay[]> {
  const [entries, notes, photos, sessions, food, water, events, shared, feed, bucket] =
    await Promise.all([
      database.journal_entries.where('date').between(from, today, true, true).toArray(),
      database.journal_notes.where('date').between(from, today, true, true).toArray(),
      database.journal_photos.toArray(),
      database.workout_sessions.toArray(),
      database.food_log.where('date').between(from, today, true, true).toArray(),
      database.water_log.where('date').between(from, today, true, true).toArray(),
      database.events.where('date').between(from, today, true, true).toArray(),
      database.household_events.where('date').between(from, today, true, true).toArray(),
      database.feed_events.where('date').between(from, today, true, true).toArray(),
      database.bucket_items.toArray(),
    ]);

  const latest = new Map<string, LocalJournalEntry>();
  for (const entry of entries) {
    if (entry.deletedAt) continue;
    const known = latest.get(entry.date);
    if (!known || entry.updatedAt > known.updatedAt) latest.set(entry.date, entry);
  }
  const notesOf = new Map<string, LocalJournalNote[]>();
  for (const note of notes) {
    if (note.deletedAt) continue;
    notesOf.set(note.date, [...(notesOf.get(note.date) ?? []), note]);
  }
  // Fotos zählen je Tag: am Tag (über irgendeine Zeile dieses Tages, zwei
  // Geräte können zwei angelegt haben) oder an einem Gedanken des Tages.
  const dateOfEntry = new Map(entries.map((row) => [row.id, row.date]));
  const dateOfNote = new Map(notes.map((row) => [row.id, row.date]));
  const photosOf = new Map<string, LocalJournalPhoto[]>();
  for (const photo of photos) {
    if (photo.deletedAt) continue;
    const date = photo.noteId
      ? dateOfNote.get(photo.noteId)
      : dateOfEntry.get(photo.journalEntryId);
    if (date) photosOf.set(date, [...(photosOf.get(date) ?? []), photo]);
  }
  const trainingsOf = new Map<string, string[]>();
  for (const session of sessions) {
    if (session.deletedAt) continue;
    const date = isoDateInZone(new Date(session.startedAt), timeZone);
    if (date < from || date > today) continue;
    trainingsOf.set(date, [...(trainingsOf.get(date) ?? []), session.title]);
  }
  const sum = <T extends { date: string; deletedAt: string | null }>(
    rows: T[],
    value: (row: T) => number,
  ) => {
    const result = new Map<string, number>();
    for (const row of rows) {
      if (row.deletedAt) continue;
      result.set(row.date, (result.get(row.date) ?? 0) + value(row));
    }
    return result;
  };
  const kcal = sum(food, (row) => row.kcal);
  const ml = sum(water, (row) => row.ml);
  const eventCount = sum(
    [...events.filter((row) => !row.cancelled), ...shared.filter((row) => !row.cancelled), ...feed],
    () => 1,
  );

  const days: TimelineDay[] = [];
  for (let date = today; date >= from; date = addDays(date as IsoDate, -1)) {
    const dayNotes = (notesOf.get(date) ?? []).sort((a, b) => a.time.localeCompare(b.time));
    const dayPhotos = photosOf.get(date) ?? [];
    days.push({
      date,
      entry: latest.get(date) ?? null,
      notes: dayNotes.length,
      firstNote: dayNotes[0] ? { title: dayNotes[0].title, body: dayNotes[0].body } : null,
      photos: dayPhotos.length,
      firstPhoto: dayPhotos[0] ?? null,
      trainings: trainingsOf.get(date) ?? [],
      kcal: kcal.has(date) ? Math.round(kcal.get(date)!) : null,
      waterMl: ml.get(date) ?? 0,
      events: eventCount.get(date) ?? 0,
      bucketDone: bucket
        .filter((row) => !row.deletedAt && row.status === 'done' && row.doneDate === date)
        .map((row) => row.title),
    });
  }
  return days;
}

/** Ein Abschnitt der Zeitleiste: eine Woche (Montag bis Sonntag). */
export interface TimelineWeek {
  /** Montag der Woche. */
  monday: string;
  /** Tage und Läufe leerer Tage, neueste zuerst. */
  items: (
    { kind: 'day'; day: TimelineDay } | { kind: 'empty'; from: string; to: string; count: number }
  )[];
}

/**
 * Nach Wochen gliedern, leere Tage zusammenfassen („3 Tage ohne Eintrag“).
 * Heute bleibt immer ein eigener Tag — dort steht die Heute-Karte.
 */
export function groupTimeline(days: TimelineDay[]): TimelineWeek[] {
  const weeks: TimelineWeek[] = [];
  for (const day of days) {
    const monday = addDays(day.date as IsoDate, 1 - weekdayOf(day.date));
    let week = weeks.at(-1);
    if (!week || week.monday !== monday) {
      week = { monday, items: [] };
      weeks.push(week);
    }
    if (!isEmptyDay(day)) {
      week.items.push({ kind: 'day', day });
      continue;
    }
    const last = week.items.at(-1);
    if (last?.kind === 'empty') {
      last.from = day.date;
      last.count += 1;
    } else {
      week.items.push({ kind: 'empty', from: day.date, to: day.date, count: 1 });
    }
  }
  return weeks;
}
